import React, { ComponentProps, FC, createContext } from 'react';
import { Types as ThemeTypes, assignInlineVars } from '@fiverr-private/theme';
import { Stack } from '@fiverr-private/layout_components';
import { createUseContext, sizeMapper, varMapper } from '../../../../utils';
import { PortfolioProjectCardResponsiveSize } from '../../../PortfolioProjectCard';
import { ThumbnailLast } from './Thumbnail';
import { tAmountDefault, tAmountSm, tAmountMd, tAmountLg, tAmountXl } from './Layout.css';

export const DEFAULT_CARD_SIZE: PortfolioProjectCardResponsiveSize = 'sm';

export const MAX_VERTICAL_THUMBNAILS_AMOUNT = 3;

export const DEFAULT_HORIZONTAL_THUMBNAILS_AMOUNT = 3;

export const DEFAULT_HORIZONTAL_THUMBNAILS_POSITION: ThumbnailsPosition = 'bottom';

export type ThumbnailsPosition = 'bottom' | 'right';

export type ResponsiveThumbnailsPosition = ThemeTypes.ResponsiveObject<ThumbnailsPosition>;

export type ThumbnailsAmount = number;

export type ResponsiveThumbnailsAmount = ThemeTypes.ResponsiveObject<ThumbnailsAmount>;

export type ThumbnailsSize = 'lg' | 'md';

export type ResponsiveThumbnailsSize = ThemeTypes.ResponsiveObject<ThumbnailsSize>;

export interface LayoutContextValue {
  /**
   * Size of the main card layout. This is a responsive object (Penta Styling API). This will also affect the size of the thumbnails. if their position is "right", so the will be aligned to same component height.
   */
  cardSize: PortfolioProjectCardResponsiveSize;
  /**
   * Position of the thumbnails cards in the layout relative to main card. This is a responsive object (Penta Styling API).
   */
  thumbnailsPosition: ResponsiveThumbnailsPosition;
  /**
   * Amount of thumbnails cards to display. This will applied only in "bottom" thumbnails position.
   * Otherwise, it will be ignored, and 3 value will be used. This is a responsive object (Penta Styling API).
   */
  thumbnailsAmount: ResponsiveThumbnailsAmount;
}

export const LayoutContext = createContext<LayoutContextValue | null>(null);

export const useLayoutContext = createUseContext(LayoutContext);

export interface LayoutProps extends Partial<LayoutContextValue>, Pick<ComponentProps<typeof Stack>, 'className'> {}

export const Layout: FC<LayoutProps> = ({
  children,
  className,
  cardSize = DEFAULT_CARD_SIZE,
  thumbnailsPosition = DEFAULT_HORIZONTAL_THUMBNAILS_POSITION,
  thumbnailsAmount = DEFAULT_HORIZONTAL_THUMBNAILS_AMOUNT,
}) => {
  const normalizedCardSize =
    typeof cardSize === 'string' ? cardSize : { default: cardSize.default || DEFAULT_CARD_SIZE, ...cardSize };

  const normalizedThumbnailsPosition: ResponsiveThumbnailsPosition =
    typeof thumbnailsPosition === 'string'
      ? thumbnailsPosition
      : { default: thumbnailsPosition.default || DEFAULT_HORIZONTAL_THUMBNAILS_POSITION, ...thumbnailsPosition };

  const normalizedThumbnailsAmount =
    typeof thumbnailsAmount === 'number'
      ? thumbnailsAmount
      : { default: thumbnailsAmount.default || DEFAULT_HORIZONTAL_THUMBNAILS_AMOUNT, ...thumbnailsAmount };

  return (
    <LayoutContext.Provider
      value={{
        cardSize: normalizedCardSize,
        thumbnailsPosition: normalizedThumbnailsPosition,
        thumbnailsAmount: normalizedThumbnailsAmount,
      }}
    >
      <Stack
        gap="4"
        className={className}
        direction={sizeMapper(normalizedThumbnailsPosition, (position) => (position === 'bottom' ? 'column' : 'row'))}
      >
        {children}
      </Stack>
    </LayoutContext.Provider>
  );
};

export interface LayoutThumbnailsProps {
  amount?: number;
}

export const LayoutThumbnails: FC<LayoutThumbnailsProps> = ({ children, amount }) => {
  const { cardSize, thumbnailsPosition, thumbnailsAmount } = useLayoutContext();

  const amountVars = varMapper(thumbnailsAmount, {
    defaultVar: tAmountDefault,
    smVar: tAmountSm,
    mdVar: tAmountMd,
    lgVar: tAmountLg,
    xlVar: tAmountXl,
  });

  return (
    <>
      <Stack
        hidden={sizeMapper(thumbnailsPosition, (position) => position === 'bottom')}
        direction={sizeMapper(thumbnailsPosition, (position) => (position === 'bottom' ? 'row' : 'column'))}
        flexShrink={sizeMapper(thumbnailsPosition, (position) => (position === 'bottom' ? '1' : '0'))}
        flexBasis={sizeMapper(cardSize, (size) => (size === 'lg' ? '189px' : '131px'))}
        style={assignInlineVars(amountVars)}
      >
        <>
          {Array.isArray(children) && children.length > MAX_VERTICAL_THUMBNAILS_AMOUNT - 1
            ? children.slice(0, MAX_VERTICAL_THUMBNAILS_AMOUNT - 1)
            : children}
          <ThumbnailLast amount={amount} variant="vertical" />
        </>
      </Stack>
      <Stack
        overflow="hidden"
        hidden={sizeMapper(thumbnailsPosition, (position) => position === 'right')}
        style={assignInlineVars(amountVars)}
      >
        {children &&
          (Array.isArray(children)
            ? children.map((child) => React.cloneElement(child, { variant: 'horizontal' }))
            : React.cloneElement(children as any, { variant: 'horizontal' }))}
        <ThumbnailLast variant="horizontal" amount={amount} />
      </Stack>
    </>
  );
};
